import { Dispatch } from "redux";
import { LocaleTypes } from "@iddataweb/idw-common-ui/enums";
import { resources } from "@constants/hardcoded-strings";
import { formatAdvancedWorkflowDetailsConfiguration } from "@formatters/advanced-workflow-details-configuration/advanced-workflow-details-configuration-formatter";
import { AxnAttributeTypeField, AxnAvailablePropertyCategory, AxnCompany, AxnIdentityProvider, AxnRelyingParty, AxnServiceOfferingDataSource } from "@idw-types/data/reference-data/axn";
import { AdvancedWorkflowDetailsConfiguration, FormattedAdvancedWorkflowDetailsConfiguration, FormattedWorkflowDetailsConfiguration } from "@idw-types/data/workflow-details-configuration";
import { setAdvancedWorkflowDetails } from "@redux/actions/workflow-details-actions";
import { updateWorkflowDetailsRequest } from "@requests/service-configuration";
import { displaySuccessModal, logAndDisplayErrorModal } from "@utils/app-alert-util";
import { AppliedProfile } from "@idw-types/data/applied-profile/applied-profile";
import { IdwProfile, IdwProfileProperty } from "@idw-types/data/idw-profile";
import {
    AXNUI_CONTENT_PROPERTY_TYPE_ID,
    AXNUI_LAYOUT_PROPERTY_TYPE_ID,
    AXNUI_THEME_PROPERTY_TYPE_ID,
    DCUI_CONTENT_PROPERTY_TYPE_ID,
    DCUI_THEME_PROPERTY_TYPE_ID
} from "@constants/verification-service-details-constants";
import { performUnmergeForContentOuter, performUnmergeForForMultiLanguageContentOuter } from "@utils/content-util";
import { AppliedProfileTypes } from "@enums/applied-profile-types";
import {
    DEFAULT_AXNUI_CONTENT,
    DEFAULT_AXNUI_LAYOUT,
    DEFAULT_AXNUI_THEME,
    DEFAULT_DCUI_CONTENT,
    DEFAULT_DCUI_THEME
} from "@iddataweb/idw-common-ui/constants";
import { FormattedRpServiceConfiguration } from "@idw-types/data/rp-service-configuration";
import { formatRpServiceConfigurations, unformatRpServiceConfigurations } from "@formatters/rp-services/rp-service-configuration-formatter";
import { ServiceOffering, FallbackServiceMapping, AttributeType, AttributeLabel, AttributeClassification, ApAssertion, Assertion, DeployedPolicyComponent, RpServiceAttributeProperty, RpServiceAttributePropertyMap } from "@idw-types/data/reference-data/generic";
import { ApAssertionStatusMap } from "@idw-types/data/ap-assertion-status-map/ap-assertion-status-map";

export const updateWorkflowDetailsReduxRequest = (dispatch: Dispatch) => async (
    workflowDetails: FormattedWorkflowDetailsConfiguration,
    rpServices: Array<FormattedRpServiceConfiguration>,
    axnRelyingParties: Array<AxnRelyingParty>,
    axnCompanies: Array<AxnCompany>,
    axnAvailablePropertyCategories: Array<AxnAvailablePropertyCategory>,
    availableLanguages: Array<LocaleTypes>,
    identityProviders: Array<AxnIdentityProvider>,
    availablePropertyCategories: Array<AxnAvailablePropertyCategory>,
    serviceOfferings: Array<ServiceOffering>,
    serviceOfferingDataSources: Array<AxnServiceOfferingDataSource>,
    fallbackServiceMappings: Array<FallbackServiceMapping>,
    attributeTypes: Array<AttributeType>,
    attributeLabels: Array<AttributeLabel>,
    attributeClassifications: Array<AttributeClassification>,
    apAssertions: Array<ApAssertion>,
    assertions: Array<Assertion>,
    deployedPolicyComponents: Array<DeployedPolicyComponent>,
    axnAttributeTypeFields: Array<AxnAttributeTypeField>,
    rpServiceAttributeProperties: Array<RpServiceAttributeProperty>,
    rpServiceAttributePropertyMaps: Array<RpServiceAttributePropertyMap>,
    assertionStatusMaps: Array<ApAssertionStatusMap>,
    assertionScoreServiceOfferingIDs: Array<string>,
    trustScoreServiceOfferingIDs: Array<string>
): Promise<FormattedAdvancedWorkflowDetailsConfiguration> => {
    const localeTypeValues = Object.values(LocaleTypes);
    const appliedProfiles: Array<AppliedProfile> = workflowDetails.appliedProfiles.map(appliedProfile => {
        const profileProperties: Array<IdwProfileProperty> = appliedProfile.profile.profileProperties
            .filter(pp => pp.addedToProfile)
            .map(profileProperty => {
                let value = profileProperty.value;
                if (appliedProfile.type === AppliedProfileTypes.WORKFLOW) {
                    if (profileProperty.availableProperty?.id === AXNUI_CONTENT_PROPERTY_TYPE_ID) {
                        try {
                            const parsedJSON = profileProperty.value ? JSON.parse(profileProperty.value) : {};
                            if (Object.keys(parsedJSON).every(key => localeTypeValues.includes(key as LocaleTypes))) {
                                value = performUnmergeForForMultiLanguageContentOuter(DEFAULT_AXNUI_CONTENT, profileProperty.value, availableLanguages);
                            } else {
                                value = performUnmergeForContentOuter(DEFAULT_AXNUI_CONTENT[LocaleTypes.ENGLISH], profileProperty.value);
                            }
                        } catch (error) {
                            console.log(`error parsing JSON for AXNUI Content profile property value: ${profileProperty.value}, error: ${error}`);
                        }
                    } else if (profileProperty.availableProperty?.id === AXNUI_LAYOUT_PROPERTY_TYPE_ID) {
                        value = performUnmergeForContentOuter(DEFAULT_AXNUI_LAYOUT, profileProperty.value);
                    } else if (profileProperty.availableProperty?.id === AXNUI_THEME_PROPERTY_TYPE_ID) {
                        value = performUnmergeForContentOuter(DEFAULT_AXNUI_THEME, profileProperty.value);
                    } else if (profileProperty.availableProperty?.id === DCUI_CONTENT_PROPERTY_TYPE_ID) {
                        try {
                            const parsedJSON = profileProperty.value ? JSON.parse(profileProperty.value) : {};
                            if (Object.keys(parsedJSON).every(key => localeTypeValues.includes(key as LocaleTypes))) {
                                value = performUnmergeForForMultiLanguageContentOuter(DEFAULT_DCUI_CONTENT, profileProperty.value, availableLanguages);
                            } else {
                                value = performUnmergeForContentOuter(DEFAULT_DCUI_CONTENT[LocaleTypes.ENGLISH], profileProperty.value);
                            }
                        } catch (error) {
                            console.log(`error parsing JSON for DCUI Content profile property value: ${profileProperty.value}, error: ${error}`);
                        }
                    } else if (profileProperty.availableProperty?.id === DCUI_THEME_PROPERTY_TYPE_ID) {
                        value = performUnmergeForContentOuter(DEFAULT_DCUI_THEME, profileProperty.value);
                    }
                }
                return {
                    availablePropertyID: profileProperty.availableProperty?.id,
                    id: profileProperty.id,
                    value: value
                };
            });
        const profile: IdwProfile = {
            companyID: appliedProfile.profile.company?.id || null,
            id: appliedProfile.profile.id,
            name: appliedProfile.profile.name,
            profileProperties: profileProperties
        };
        return {
            id: appliedProfile.id,
            nickname: appliedProfile.nickname,
            precedence: appliedProfile.precedence.toFixed(1),
            profile: profile,
            referrerID: appliedProfile.referrerID,
            triggerProfileID: appliedProfile.triggerProfileID,
            type: appliedProfile.type
        }
    });

    const unformattedRpServices = unformatRpServiceConfigurations(rpServices, availableLanguages);

    const finalWorkflowDetails: AdvancedWorkflowDetailsConfiguration = {
        workflow: {
            rpServiceWorkflows: workflowDetails.rpServiceWorkflows,
            name: workflowDetails.name,
            status: workflowDetails.status,
            companyID: workflowDetails.company?.id,
            relyingPartyID: workflowDetails.relyingParty?.id,
            sku: workflowDetails.sku,
            workflowKey: workflowDetails.workflowKey,
            workflowVariants: workflowDetails.workflowVariants,
            appliedProfiles: appliedProfiles,
            permissions: workflowDetails.permissions,
            hasDCUIService: workflowDetails.hasDCUIService,
            id: workflowDetails.id
        },
        rpServices: unformattedRpServices
    };

    const { data, error, body } = await updateWorkflowDetailsRequest(`${workflowDetails.id}`, finalWorkflowDetails);
    if (data?.transferObject) {
        const { rpServices } = data.transferObject;
        const services = formatRpServiceConfigurations(
            rpServices,
            identityProviders,
            axnRelyingParties,
            availablePropertyCategories,
            serviceOfferings,
            serviceOfferingDataSources,
            fallbackServiceMappings,
            attributeTypes,
            attributeLabels,
            attributeClassifications,
            apAssertions,
            assertions,
            deployedPolicyComponents,
            axnAttributeTypeFields,
            rpServiceAttributeProperties,
            rpServiceAttributePropertyMaps,
            assertionStatusMaps,
            assertionScoreServiceOfferingIDs,
            trustScoreServiceOfferingIDs,
            axnCompanies
        );


        const formattedWorkflowDetails = formatAdvancedWorkflowDetailsConfiguration(
            data.transferObject,
            axnRelyingParties,
            axnCompanies,
            axnAvailablePropertyCategories,
            services
        );

        // permissions and hasDCUIService come back incorrectly from save request
        dispatch(setAdvancedWorkflowDetails(formattedWorkflowDetails, true));
        displaySuccessModal(
            dispatch,
            resources.requests.updateWorkflowDetails.success.title,
            resources.requests.updateWorkflowDetails.success.message
        );
        return formattedWorkflowDetails;
    } else {
        if (error) {
            logAndDisplayErrorModal(
                dispatch,
                error,
                resources.requests.updateWorkflowDetails.error.title,
                resources.requests.updateWorkflowDetails.error.message,
                error,
                null,
                body
            );
        }
    }
    return null;
};